import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CACHE_KEY = 'mycryptoplanner_exchange_rates';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

interface RateCache {
  rates: Record<string, number>;
  fetchedAt: number;
}

function loadCache(): RateCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RateCache;
  } catch {
    return null;
  }
}

function saveCache(rates: Record<string, number>) {
  localStorage.setItem(CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() }));
}

/** The app's cached FX rates (USD-based: `rates[X]` = units of X per 1 USD), or
 *  null if none have been cached yet. Same source `useExchangeRates` /
 *  `CurrencyContext` use — NOT a separate FX provider. */
export function loadCachedRates(): Record<string, number> | null {
  return loadCache()?.rates ?? null;
}

/** Convert `amount` from `fromCurrency` into USD (the app's internal storage
 *  currency) using the cached FX rates. USD passes through unchanged. Returns
 *  null when the currency is unknown or its rate is unavailable, so callers can
 *  prefer NO value over a wrong one rather than storing a mis-denominated number. */
export function convertToUsd(amount: number, fromCurrency: string): number | null {
  const code = (fromCurrency || '').toUpperCase();
  if (!code) return null;
  if (code === 'USD') return amount;
  const rate = loadCachedRates()?.[code];
  if (!rate || rate <= 0 || !isFinite(rate)) return null;
  return amount / rate;
}

export function useExchangeRates() {
  /**
   * Seeded from the cache SYNCHRONOUSLY, not in the effect below.
   *
   * Starting at `{ USD: 1 }` means `rates[currencyCode]` is undefined for one
   * render, so `rate` falls back to 1 and every figure paints at USD magnitude
   * under a non-USD symbol — "A$100.00" for something worth A$152.00 — until the
   * effect lands. That was survivable while USD was the default and only users who
   * had opted into another currency saw it. With AUD as the default it would be
   * everyone's first paint, so the cache is read up front instead.
   *
   * A stale cache is still far better than 1.0 here, and the effect refreshes it
   * immediately either way.
   */
  const [rates, setRates]     = useState<Record<string, number>>(
    () => loadCache()?.rates ?? { USD: 1 }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const fetch = useCallback(async () => {
    const cached = loadCache();
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
      setRates(cached.rates);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(
        'https://api.exchangerate-api.com/v4/latest/USD',
        { timeout: 8_000 }
      );
      const r = data.rates as Record<string, number>;
      saveCache(r);
      setRates(r);
      setError(null);
    } catch {
      const cached2 = loadCache();
      if (cached2) setRates(cached2.rates);
      setError('Could not fetch exchange rates. Using cached rates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { rates, loading, error };
}
